import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Settings as SettingsIcon, CheckCircle, Store, Trash2, Plus, X, Wrench } from 'lucide-react';
import Layout from './Layout';
import { useStore } from '../context/StoreContext';
import { useAlert } from '../context/AlertContext';
import './Management.css';

const Settings = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { getStore, updateStore, user, resetAllData } = useStore();
    const { showAlert } = useAlert();
    const [storeName, setStoreName] = useState('');
    const [managerId, setManagerId] = useState('');
    const [password, setPassword] = useState('');
    const [operatingType, setOperatingType] = useState('direct'); // direct | rental
    const [itemNames, setItemNames] = useState(['', '', '', '', '', '', '']);
    const [facilityItems, setFacilityItems] = useState([
        { name: 'POS/결제기기', status: 'normal', note: '' },
        { name: '주방설비 (튀김기/그릴)', status: 'normal', note: '' },
        { name: '냉장/냉동고', status: 'normal', note: '' },
        { name: '조명/전기시설', status: 'normal', note: '' },
        { name: '기타 1', status: 'normal', note: '' },
        { name: '기타 2', status: 'normal', note: '' },
        { name: '기타 3', status: 'normal', note: '' }
    ]);
    const [isSaved, setIsSaved] = useState(false);

    // 보안 권한 체크: 매니저는 접근 불가
    useEffect(() => {
        if (user && user.role !== 'admin') {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    useEffect(() => {
        const store = getStore(id);
        if (store) {
            setStoreName(store.name);
            setManagerId(store.managerId || `store${id}`);
            setPassword(store.password || '1234');
            setOperatingType(store.operatingType || 'direct');
            setItemNames(store.items || ['', '', '', '', '', '', '']);
            
            // 시설 항목 로드
            if (store.facilityData?.items && store.facilityData.items.length > 0) {
                setFacilityItems(store.facilityData.items);
            }
        }
    }, [id, getStore]);

    const handleItemNameChange = (index, value) => {
        const newNames = [...itemNames];
        newNames[index] = value;
        setItemNames(newNames);
    };

    const addItem = () => {
        if (itemNames.length >= 40) {
            showAlert('최대 40개 항목까지만 등록할 수 있습니다.');
            return;
        }
        setItemNames([...itemNames, '']);
    };

    const removeItem = (index) => {
        if (itemNames.length <= 1) {
            showAlert('최소 1개 이상의 항목은 유지해야 합니다.');
            return;
        }
        const newNames = itemNames.filter((_, i) => i !== index);
        setItemNames(newNames);
    };

    // 시설 항목 관리 함수들
    const handleFacilityNameChange = (index, value) => {
        const newFacilities = [...facilityItems];
        newFacilities[index] = { ...newFacilities[index], name: value };
        setFacilityItems(newFacilities);
    };

    const addFacilityItem = () => {
        if (facilityItems.length >= 15) {
            showAlert('시설 항목은 최대 15개까지만 등록할 수 있습니다.');
            return;
        }
        setFacilityItems([...facilityItems, { name: '', status: 'normal', note: '' }]);
    };

    const removeFacilityItem = (index) => {
        if (facilityItems.length <= 1) {
            showAlert('최소 1개 이상의 시설 항목은 유지해야 합니다.');
            return;
        }
        setFacilityItems(facilityItems.filter((_, i) => i !== index));
    };

    const handleSave = () => {
        updateStore(id, {
            name: storeName,
            managerId: managerId,
            password: password,
            operatingType: operatingType,
            items: itemNames,
            facilityData: {
                items: facilityItems
            }
        });
        setIsSaved(true);
        setTimeout(() => setIsSaved(false), 2000);
    };

    const handleReset = () => {
        if (window.confirm('⚠️ 경고: 모든 매장의 매출, 재고, 품목 설정 및 저장된 모든 데이터가 초기화됩니다. 이 작업은 되돌릴 수 없습니다. 진행하시겠습니까?')) {
            if (window.confirm('정말로 모든 데이터를 삭제하시겠습니까? 다시 한번 확인해 주세요.')) {
                const masterKey = window.prompt('보안을 위해 상위 비밀번호(Master Key)를 입력해주세요.');
                if (masterKey === 'kkh1234') {
                    resetAllData();
                    showAlert('✅ 모든 데이터가 초기화되었습니다.');
                } else if (masterKey !== null) {
                    showAlert('상위 비밀번호가 일치하지 않습니다. 권한이 없습니다.');
                }
            }
        }
    };

    return (
        <Layout>
            <div className="management-content">
                <div className="card-header">
                    <h1>매장 설정 ({id}번 매장: {storeName})</h1>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        {id === '1' ? (
                            <div className="submit-badge" style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', border: '1px solid #38bdf8' }}>
                                <Store size={16} /> 마스터 매장
                            </div>
                        ) : (
                            <div className="submit-badge pending">
                                개별 설정 모드
                            </div>
                        )}
                        {isSaved && <span className="submit-badge"><CheckCircle size={16} /> 설정 저장됨</span>}
                    </div>
                </div>

                <div className="glass-card management-card">
                    <div className="settings-header">
                        <Store size={24} color="#38bdf8" />
                        <h3>매장 기본 정보</h3>
                    </div>

                    <div className="settings-form" style={{ marginBottom: '40px' }}>
                        <div className="input-group">
                            <label>매장 이름</label>
                            <input
                                type="text"
                                value={storeName}
                                placeholder="매장 이름을 입력하세요"
                                onChange={(e) => setStoreName(e.target.value)}
                                style={{ fontSize: '1.2rem', fontWeight: '700' }}
                            />
                        </div>
                        <div className="input-group" style={{ marginTop: '20px' }}>
                            <label>운영 형태</label>
                            <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '500' }}>
                                    <input 
                                        type="radio" 
                                        name="operatingType" 
                                        value="direct" 
                                        checked={operatingType === 'direct'} 
                                        onChange={(e) => setOperatingType(e.target.value)}
                                    />
                                    직영 매장
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '500' }}>
                                    <input 
                                        type="radio" 
                                        name="operatingType" 
                                        value="rental" 
                                        checked={operatingType === 'rental'} 
                                        onChange={(e) => setOperatingType(e.target.value)}
                                    />
                                    임대 매장
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="settings-header">
                        <SettingsIcon size={24} color="#38bdf8" />
                        <h3>매니저 계정 설정</h3>
                    </div>
                    <p className="settings-desc">로그인 시 사용할 아이디와 비밀번호를 설정하세요.</p>

                    <div className="settings-form" style={{ marginBottom: '40px' }}>
                        <div className="input-field">
                            <label>매니저 아이디</label>
                            <input
                                type="text"
                                value={managerId}
                                placeholder="아이디 입력"
                                onChange={(e) => setManagerId(e.target.value)}
                            />
                        </div>
                        <div className="input-field">
                            <label>비밀번호</label>
                            <input
                                type="text"
                                value={password}
                                placeholder="비밀번호 입력"
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="settings-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <SettingsIcon size={24} color="#38bdf8" />
                            <h3>핵심 품목 이름 설정 (최대 40개)</h3>
                        </div>
                        <button
                            onClick={addItem}
                            className="submit-badge"
                            style={{ background: 'rgba(56, 189, 248, 0.2)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.5)', cursor: 'pointer', padding: '6px 14px' }}
                        >
                            <Plus size={16} style={{ marginRight: '4px' }} /> 항목 추가
                        </button>
                    </div>
                    <p className="settings-desc">이 매장에서 관리할 품목들의 이름을 정해주세요. 발주관리 및 재고현황에 동일하게 적용됩니다.</p>

                    <div className="settings-form" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                        {itemNames.map((name, index) => (
                            <div key={index} className="input-field" style={{ position: 'relative' }}>
                                <label>품목 {index + 1} 이름</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <input
                                        type="text"
                                        value={name}
                                        placeholder={`품목 ${index + 1}의 이름`}
                                        onChange={(e) => handleItemNameChange(index, e.target.value)}
                                        style={{ flex: 1 }}
                                    />
                                    <button
                                        onClick={() => removeItem(index)}
                                        style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#ef4444', borderRadius: '8px', padding: '0 8px', cursor: 'pointer' }}
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="settings-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '40px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Wrench size={24} color="#38bdf8" />
                            <h3>시설 관리 항목 설정 (최대 15개)</h3>
                        </div>
                        <button
                            onClick={addFacilityItem}
                            className="submit-badge"
                            style={{ background: 'rgba(56, 189, 248, 0.2)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.5)', cursor: 'pointer', padding: '6px 14px' }}
                        >
                            <Plus size={16} style={{ marginRight: '4px' }} /> 항목 추가
                        </button>
                    </div>
                    <p className="settings-desc">매장에서 점검할 시설 기기/항목들의 이름을 정해주세요. 시설 관리 페이지에 즉시 반영됩니다.</p>

                    <div className="settings-form" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                        {facilityItems.map((item, index) => (
                            <div key={index} className="input-field" style={{ position: 'relative' }}>
                                <label>시설 항목 {index + 1} 이름</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <input
                                        type="text"
                                        value={item.name}
                                        placeholder={`시설 항목 ${index + 1}의 이름`}
                                        onChange={(e) => handleFacilityNameChange(index, e.target.value)}
                                        style={{ flex: 1 }}
                                    />
                                    <button
                                        onClick={() => removeFacilityItem(index)}
                                        style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#ef4444', borderRadius: '8px', padding: '0 8px', cursor: 'pointer' }}
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginTop: '40px' }}>
                        <button className="full-width-button" onClick={handleSave} style={{ flex: 1 }}>
                            <Save size={18} style={{ marginRight: '8px' }} /> 매장 정보 저장하기
                        </button>
                    </div>

                    <div style={{ marginTop: '48px', paddingTop: '32px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <div className="settings-header">
                            <Trash2 size={24} color="#ff4d4d" />
                            <h3 style={{ color: '#ff4d4d' }}>데이터 초기화</h3>
                        </div>
                        <p className="settings-desc">테스트 중 용량이 부족하거나 데이터를 처음부터 다시 쌓고 싶을 때 사용하세요.</p>
                        <button
                            className="full-width-button"
                            onClick={handleReset}
                            style={{
                                marginTop: '16px',
                                background: 'rgba(255, 77, 77, 0.1)',
                                border: '1px solid #ff4d4d',
                                color: '#ff4d4d'
                            }}
                        >
                            <Trash2 size={18} style={{ marginRight: '8px' }} /> 모든 데이터 전체 초기화
                        </button>
                    </div>
                </div>


                <div className="glass-card info-card">
                    <h4>💡 설정 팁</h4>
                    <p>각 매장별로 독립적인 이름을 부여하고 관리 품목을 다르게 설정할 수 있습니다. 변경된 내용은 해당 매장의 대시보드와 재고 현황에 즉시 반영됩니다.</p>
                </div>
            </div>
        </Layout>
    );
};

export default Settings;
